import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { PaintFallbackSvg } from '~/components/image-selection-tile/components/fallback-svgs/paint-fallback-svg';
import { SelectionContainer } from '~/components/image-selection-tile/style/tile-selection-section-style';
import { TilesSelection } from '~/components/image-selection-tile/tiles-selection';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { FeatureCategory, FeatureOrPackage, SelectableOption } from '~/graphql/types';
import { state } from '~/storage';

export function BodyPaint({ step, total }: { step: number; total: number }) {
	const { exteriorColor, bodyColor, roofColor } = useCatalog();
	const { loadingFlags, changeConfiguration, isItemLoading, isItemChecked, carIdentifier } = useConfigurator();
	const [activeFilters, setActiveFilters] = useAtom(state.activeFilters);
	const { feature } = useVATConfig();
	const { config } = useMarketConfig();
	const { getName } = useDebugMode();
	const { i18n } = useI18n();

	const trackSelectionClickEvent = useCallback(
		(item: FeatureOrPackage, selected: boolean) => {
			const filters = activeFilters?.bodyPaint.length > 0 ? activeFilters?.bodyPaint : 'all';

			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: 'click to change configuration',
				label: item.name,
				elementName: 'image',
				value: `selection feature app: bodyPaint: ${filters}`,
				category: 'bodyPaint',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[activeFilters?.bodyPaint, step, total],
	);

	const { items, filters } = useMemo(() => {
		const hasContrastColors = roofColor && roofColor?.length > 0;
		// If there are roof colors, use the body color data for body paint. If no roof colors are available, then take colors from exterior colors.
		const bodyPaint = hasContrastColors ? bodyColor : exteriorColor;
		if (!bodyPaint) return { items: [], filters: [] };

		const mapItem = (item: (typeof bodyPaint)[0]) => ({
			name: getName(item) ?? ' ',
			key: item.featureId,
			onSelect: () => {
				trackSelectionClickEvent(item.__item, false);
				changeConfiguration(item as SelectableOption<FeatureCategory>);
			},
			loading: isItemLoading(item),
			readonly: !item.status.changeable,
			familyName: item.familyName,
			familyId: item.familyId,
			showRenderDisclaimer: item.__item.family?.id === 'color-type:exclusive',
			audiExclusive: item.__item.featureType === 'EXCLUSIVE_EXTERIOR_COLOR',
			standard: item.status.standard,
			imgUrl: item.imgUrl,
			selected: isItemChecked(item),
			price: config.showPrices ? { totalPrice: item.totalPrice, feature } : undefined,
			disclaimerHeadline: item.__item.texts?.disclaimerHeadline || undefined,
			disclaimerContent: item.__item.texts?.disclaimerText || undefined,
			disclaimerStartDate: item.__item.texts?.disclaimerStartDate || undefined,
			disclaimerEndDate: item.__item.texts?.disclaimerEndDate || undefined,
		});

		// Map items for the tile selection
		const mappedItems = bodyPaint.map((onePaint) => mapItem(onePaint));

		// Check for audi exclusive colors. Only used when no contrast colors are available. If contrast colors are available this will be overwritten.
		let hasExclusiveColors: boolean | undefined = mappedItems?.some((paint) => paint.audiExclusive);

		if (hasContrastColors) {
			// Exclusive colors are not included in bodyColors, so we need to check them in exteriorColors
			hasExclusiveColors = exteriorColor?.some(
				(paint) => paint.__item.featureType === 'EXCLUSIVE_EXTERIOR_COLOR',
			);

			if (hasExclusiveColors) {
				// Add all the exclusive colors to the mappedItems array
				const exteriorExclusiveColors = exteriorColor
					?.filter((paint) => paint.__item.featureType === 'EXCLUSIVE_EXTERIOR_COLOR')
					.map((onePaint) => mapItem(onePaint));

				exteriorExclusiveColors && mappedItems.push(...exteriorExclusiveColors);
			}
		}

		const colorFilters: string[] = [];
		if (hasExclusiveColors) {
			// Create filters if there are exclusive colors
			colorFilters.push(i18n('ngc.filter.colors'), i18n('ngc.filter.exclusiveColors'));

			// Flag "individual paintwork" (Individuallackierungen) as exclusive colors and move them to the end of the list
			const exclusiveColorPlaceholders = mappedItems.filter((paint) => paint.familyId === 'color-type:exclusive');
			exclusiveColorPlaceholders.forEach((paint) => {
				paint.audiExclusive = true;
				const index = mappedItems.indexOf(paint);
				mappedItems.splice(index, 1);
				mappedItems.push(paint);
			});
		}

		// If an Audi exclusive color is selected, deselect the "individual paintwork" (Individuallackierungen)
		const isSelectedPaintAudiExclusive = mappedItems.find(
			(paint) => paint.selected && paint.familyId !== 'color-type:exclusive',
		)?.audiExclusive;
		const individualPaintwork = mappedItems.filter((paint) => paint.familyId === 'color-type:exclusive');
		if (isSelectedPaintAudiExclusive && individualPaintwork.length)
			individualPaintwork.forEach((paint) => {
				paint.selected = false;
			});

		// Sort colors by price -> lowest price first
		mappedItems.sort((a, b) => {
			const priceA = a.price?.totalPrice?.value;
			const priceB = b.price?.totalPrice?.value;
			if (priceA !== undefined && priceB !== undefined) {
				return priceA - priceB;
			}
			return 0;
		});

		return {
			filters: colorFilters,
			items: mappedItems,
		};
	}, [
		JSON.stringify(exteriorColor),
		JSON.stringify(roofColor),
		JSON.stringify(bodyColor),
		i18n,
		getName,
		isItemLoading,
		isItemChecked,
		config.showPrices,
		feature,
		trackSelectionClickEvent,
		changeConfiguration,
		JSON.stringify(carIdentifier),
	]);

	// This is used to track if the color items has changed (can happen when a trimline is changed). If so, the useEffect for pre-selecting the correct filter needs to be triggered again.
	const itemKeys = useMemo(() => {
		return JSON.stringify(items.map((item) => item.key));
	}, [JSON.stringify(items)]);

	useEffect(() => {
		if (filters?.length > 0) {
			// If there are Audi exclusive colors, set the active filter to basic colors
			const hasAudiExclusive = items.some((item) => item.audiExclusive);
			const isAudiExclusiveSelected = items?.find((item) => item.selected)?.audiExclusive;

			setActiveFilters((prev) => ({
				...prev,
				bodyPaint: hasAudiExclusive
					? [i18n(`ngc.filter.${isAudiExclusiveSelected ? 'exclusiveColors' : 'colors'}`)]
					: [],
			}));
		}
		// CarIdentifier to Trigger only after selected color change not just on any color item change
	}, [carIdentifier.exteriorColor, JSON.stringify(filters), itemKeys]);

	return (
		<SelectionContainer items={exteriorColor?.length}>
			<TilesSelection
				id="bodyPaint"
				items={items}
				filters={filters}
				fallbackSVG={PaintFallbackSvg()}
				isLoading={loadingFlags.checkingConflicts}
				step={step}
				totalSteps={total}
				tileHorizontalCenter
			/>
		</SelectionContainer>
	);
}
